import {
  validateAllowedValue,
  validatePropertyDefaultValue,
  validatePropertyDescription,
  validatePropertyName,
} from '@github-ui/custom-properties-editing/validate'
import type {PropertyDefinition, ValueType} from '@github-ui/custom-properties-types'
import type {OnDropArgs} from '@github-ui/drag-and-drop'
import {DragAndDrop, useDragAndDrop} from '@github-ui/drag-and-drop'
import {propertyDefinitionSettingsPath} from '@github-ui/paths'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import sudo from '@github-ui/sudo'
import {useNavigate} from '@github-ui/use-navigate'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {KebabHorizontalIcon} from '@primer/octicons-react'
import {
  ActionList,
  ActionMenu,
  Box,
  Button,
  Checkbox,
  FormControl,
  IconButton,
  Label,
  Select,
  Textarea,
  TextInput,
} from '@primer/react'
import {useCallback, useRef, useState} from 'react'

import {useCurrentOrg} from '../contexts/CurrentOrgRepoContext'
import {useSetFlash} from '../contexts/FlashContext'
import {definitionTypeLabels} from '../helpers/definition-type-labels'
import {useFormField} from '../hooks/use-form-field'

interface Props {
  definition?: PropertyDefinition
  existingPropertyNames: string[]
  setFormError: (error: string) => void
}
export function PropertyDefinitionSettings({definition, existingPropertyNames, setFormError}: Props) {
  const navigate = useNavigate()
  const setFlash = useSetFlash()
  const org = useCurrentOrg()
  const [saving, setSaving] = useState(false)
  const [isRequired, setIsRequired] = useState(definition?.required || false)
  const isEditing = !!definition

  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false)

  const repoPropertiesEditingEnabled = useFeatureFlag('repo_properties_editing')
  const [repoActorsEditingAllowed, setRepoActorsEditingAllowed] = useState(
    definition?.valuesEditableBy === 'org_and_repo_actors',
  )

  const validatePropertyNameMemo = useCallback(
    (value: string) => {
      return validatePropertyName(value, existingPropertyNames)
    },
    [existingPropertyNames],
  )
  const propertyNameField = useFormField(definition?.propertyName ?? '', {
    validator: isEditing ? undefined : validatePropertyNameMemo,
  })
  const propertyNameRef = useRef<HTMLInputElement>(null)

  const propertyDescriptionField = useFormField(definition?.description ?? '', {
    validator: validatePropertyDescription,
  })
  const propertyDescriptionRef = useRef<HTMLTextAreaElement>(null)

  const definitionTypeField = useFormField(definition?.valueType ?? 'string')
  const isSingleSelect = definitionTypeField.value === 'single_select'
  const isBoolean = definitionTypeField.value === 'boolean'
  const isMultiSelect = definitionTypeField.value === 'multi_select'

  const booleanValueTypeSupported = useFeatureFlag('boolean_custom_property_type')
  const multiSelectValueTypeSupported = useFeatureFlag('custom_properties_multi_select')
  const isImmutableValueType = useFeatureFlag('custom_properties_immutable_value_type')

  const supportedTypes = Object.keys(definitionTypeLabels).filter(key => {
    if (key === 'boolean' && !booleanValueTypeSupported) {
      return false
    }
    if (key === 'multi_select' && !multiSelectValueTypeSupported) {
      return false
    }
    return true
  })

  const supportedValueTypes = supportedTypes.reduce(
    (obj, supportedType) => {
      obj[supportedType as ValueType] = definitionTypeLabels[supportedType as ValueType]
      return obj
    },
    {} as Record<ValueType, string>,
  )

  const selectedDefinitionTypeLabel =
    supportedValueTypes[definitionTypeField.value as ValueType] || supportedValueTypes.string

  const [allowedValues, setAllowedValues] = useState<string[]>(definition?.allowedValues ?? [])
  const newAllowedValueField = useFormField('', {
    validator: value => validateAllowedValue(value, new Set(allowedValues)),
  })
  const newAllowedValueRef = useRef<HTMLInputElement>(null)

  function addAllowedValue() {
    if (newAllowedValueField.validate()) {
      return newAllowedValueRef.current?.focus()
    }

    setAllowedValues([...allowedValues, newAllowedValueField.value])
    newAllowedValueField.reset()
  }

  function removeAllowedValue(index: number) {
    setAllowedValues(allowedValues.filter((_, currentIndex) => currentIndex !== index))
  }

  const propertyDefaultValueField = useFormField(definition?.defaultValue ?? '', {
    validator: value =>
      validatePropertyDefaultValue(value, definitionTypeField.value as ValueType, isRequired, allowedValues),
  })
  const propertyStringDefaultValueRef = useRef<HTMLInputElement>(null)
  const propertySingleSelectDefaultValueRef = useRef<HTMLSelectElement>(null)

  const allowedValuesMissing = (isSingleSelect || isMultiSelect) && allowedValues.length === 0

  function focusFirstInvalidField() {
    const refs = [
      {
        element: propertyNameRef.current,
        invalid: !!propertyNameField.validate(),
      },
      {
        element: propertyDescriptionRef.current,
        invalid: !!propertyDescriptionField.validate(),
      },
      {
        element: newAllowedValueRef.current,
        invalid: allowedValuesMissing,
      },
      {
        element: propertyStringDefaultValueRef.current,
        invalid: !!propertyDefaultValueField.validate(),
      },
      {
        element: propertySingleSelectDefaultValueRef.current,
        invalid: !!propertyDefaultValueField.validate(),
      },
    ]

    const firstInvalidItem = refs.find(item => item.element && item.invalid)
    firstInvalidItem?.element?.focus()
  }

  function formCanBeSaved() {
    const hasValidationErrors = [propertyNameField, propertyDescriptionField, propertyDefaultValueField].some(field =>
      field.validate(),
    )
    if (allowedValuesMissing) {
      setFormError('Property must have at least one option.')
    }

    if (hasValidationErrors || allowedValuesMissing) {
      focusFirstInvalidField()
      return false
    }

    return true
  }

  async function saveDefinition(body: PropertyDefinition) {
    setFormError('')
    if (!formCanBeSaved()) {
      return
    }
    setSaving(true)

    if (!(await sudo())) {
      setFormError('Unauthorized')
      setSaving(false)
      return
    }

    const result = await verifiedFetchJSON(propertyDefinitionSettingsPath({org: org.login}), {
      method: 'POST',
      body,
    })

    if (result.ok) {
      setFlash(isEditing ? 'definition.updated.success' : 'definition.created.success')
      returnToList()
    } else {
      const responseBody = await result.json()
      setFormError(responseBody?.error || 'Something went wrong.')
      setSaving(false)
    }
  }

  function returnToList() {
    navigate(propertyDefinitionSettingsPath({org: org.login}))
  }

  const onDrop = ({dragMetadata, dropMetadata, isBefore}: OnDropArgs<string>) => {
    if (dragMetadata.id === dropMetadata?.id) {
      return
    }
    setAllowedValues(items => {
      const reorderedOption = items.find(item => item === dragMetadata.id)!

      return items.reduce<string[]>((newItems, item) => {
        if (item === reorderedOption) {
          return newItems
        }

        if (item !== dropMetadata?.id) {
          newItems.push(item)
        } else if (isBefore) {
          newItems.push(reorderedOption, item)
        } else if (!isBefore) {
          newItems.push(item, reorderedOption)
        }

        return newItems
      }, [])
    })
  }

  const allowableDefaultValues = isBoolean ? ['true', 'false'] : allowedValues

  return (
    <>
      <Box data-hpc sx={{mb: 4}} data-testid="settings-page-content">
        <FormControl disabled={isEditing} sx={{mb: 3, maxWidth: '285px'}}>
          <FormControl.Label>Name *</FormControl.Label>
          <TextInput
            block
            ref={propertyNameRef}
            aria-describedby="property-name-caption"
            aria-invalid={!propertyNameField.isValid()}
            value={propertyNameField.value}
            onChange={e => propertyNameField.update(e.target.value, {shouldValidate: true})}
            onBlur={propertyNameField.validate}
          />

          {propertyNameField.validationError && (
            <FormControl.Validation variant="error">{propertyNameField.validationError}</FormControl.Validation>
          )}
        </FormControl>

        <FormControl sx={{mb: 3, maxWidth: '480px'}}>
          <FormControl.Label>Description</FormControl.Label>
          <Textarea
            block
            rows={4}
            placeholder="A short description about this property"
            ref={propertyDescriptionRef}
            value={propertyDescriptionField.value}
            onChange={e => propertyDescriptionField.update(e.target.value, {shouldValidate: true})}
            onBlur={propertyDescriptionField.validate}
          />

          {propertyDescriptionField.validationError && (
            <FormControl.Validation variant="error">{propertyDescriptionField.validationError}</FormControl.Validation>
          )}
        </FormControl>

        <FormControl
          sx={{mb: isSingleSelect || isMultiSelect ? 3 : 0, maxWidth: '285px'}}
          disabled={isEditing && isImmutableValueType}
        >
          <FormControl.Label>Type</FormControl.Label>
          <ActionMenu
            open={typeDropdownOpen}
            onOpenChange={open => {
              if (isEditing && isImmutableValueType) return
              setTypeDropdownOpen(open)
            }}
          >
            <ActionMenu.Button
              inactive={isEditing && isImmutableValueType}
              aria-label={`Type: ${selectedDefinitionTypeLabel}`}
            >
              {selectedDefinitionTypeLabel}
            </ActionMenu.Button>
            <ActionMenu.Overlay>
              <ActionList selectionVariant="single">
                {Object.entries(supportedValueTypes).map(([key, label]) => (
                  <ActionList.Item
                    key={key}
                    onSelect={() => {
                      definitionTypeField.update(key)
                      propertyDefaultValueField.reset()

                      // Multi select cannot be required.
                      if (key === 'multi_select') {
                        setIsRequired(false)
                      }
                    }}
                    selected={definitionTypeField.value === key}
                  >
                    {label}
                  </ActionList.Item>
                ))}
              </ActionList>
            </ActionMenu.Overlay>
          </ActionMenu>
        </FormControl>

        {(isSingleSelect || isMultiSelect) && (
          <Box sx={{border: 'solid 1px var(--borderColor-default, var(--color-border-default))', borderRadius: '5px'}}>
            <FormControl sx={{p: 2}}>
              <FormControl.Label htmlFor="option-input" visuallyHidden>
                Options
              </FormControl.Label>
              <Box sx={{display: 'flex', gap: 2, width: '100%'}}>
                <TextInput
                  block
                  id="option-input"
                  placeholder="Add option..."
                  ref={newAllowedValueRef}
                  aria-invalid={!newAllowedValueField.isValid()}
                  value={newAllowedValueField.value}
                  onChange={e => newAllowedValueField.update(e.target.value, {shouldValidate: true})}
                  // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
                  onKeyDown={e => e.key === 'Enter' && addAllowedValue()}
                />

                <Button onClick={addAllowedValue}>Add</Button>
              </Box>
              {newAllowedValueField.validationError && (
                <FormControl.Validation variant="error">{newAllowedValueField.validationError}</FormControl.Validation>
              )}
            </FormControl>

            {allowedValues.length > 0 ? (
              <DragAndDrop
                items={allowedValues.map(value => {
                  return {id: value, title: value}
                })}
                onDrop={onDrop}
                style={{listStyleType: 'none', maxHeight: '500px', overflowY: 'auto'}}
              >
                {allowedValues.map((value, index) => (
                  <DragAndDrop.Item
                    index={index}
                    id={value}
                    title={value}
                    key={value}
                    isDisabled
                    style={{borderTop: 'solid 1px var(--borderColor-default, var(--color-border-default))'}}
                  >
                    <AllowedValueItem
                      value={value}
                      isDefaultValue={value === propertyDefaultValueField.value}
                      onDelete={() => removeAllowedValue(index)}
                      index={index}
                    />
                  </DragAndDrop.Item>
                ))}
              </DragAndDrop>
            ) : (
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  color: 'fg.muted',
                  textAlign: 'center',
                  borderTop: 'solid 1px var(--borderColor-default, var(--color-border-default))',
                }}
              >
                No options
              </Box>
            )}
          </Box>
        )}

        <Box aria-live="polite">
          {allowedValues.some(isNonSearchable) && (
            <Box sx={{p: 2, color: 'fg.muted'}}>
              Non-searchable options are values with a special meaning in the query syntax. They can be used but cannot
              be searched.
            </Box>
          )}
        </Box>

        <Box sx={{pt: 4, display: 'flex', flexDirection: 'column', gap: 3}}>
          {repoPropertiesEditingEnabled && (
            <FormControl>
              <Checkbox
                checked={repoActorsEditingAllowed}
                onChange={() => setRepoActorsEditingAllowed(!repoActorsEditingAllowed)}
              />
              <FormControl.Label>Allow repository actors to set this property</FormControl.Label>
              <FormControl.Caption>
                Repository users and apps with the repository-level &quot;custom properties&quot; fine-grained
                permission can set and update the value for their repository
              </FormControl.Caption>
            </FormControl>
          )}

          {!isMultiSelect && (
            <FormControl>
              <Checkbox checked={isRequired} onChange={() => setIsRequired(!isRequired)} />
              <FormControl.Label>Require this property for all repositories</FormControl.Label>
              <FormControl.Caption>
                Repositories that don&apos;t have an explicit value for this property will inherit the default value
              </FormControl.Caption>
            </FormControl>
          )}
        </Box>

        <Box sx={{display: 'flex', gap: 2, width: '100%'}}>
          {isRequired && (
            <FormControl sx={{pt: 1, px: 3}}>
              <FormControl.Label>Default value *</FormControl.Label>
              {isSingleSelect || isBoolean ? (
                <Select
                  placeholder="Select an option"
                  defaultValue={propertyDefaultValueField.value}
                  ref={propertySingleSelectDefaultValueRef}
                  aria-invalid={!propertyDefaultValueField.isValid()}
                  onChange={e => propertyDefaultValueField.update(e.target.value, {shouldValidate: true})}
                >
                  {allowableDefaultValues.map(value => (
                    <Select.Option key={value} value={value}>
                      {value}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <TextInput
                  block
                  value={propertyDefaultValueField.value}
                  ref={propertyStringDefaultValueRef}
                  aria-describedby="property-default-value-caption"
                  aria-invalid={!propertyDefaultValueField.isValid()}
                  onChange={e => propertyDefaultValueField.update(e.target.value, {shouldValidate: true})}
                />
              )}
              {propertyDefaultValueField.validationError && (
                <FormControl.Validation variant="error" sx={{mt: 1}}>
                  {propertyDefaultValueField.validationError}
                </FormControl.Validation>
              )}
              <FormControl.Caption>
                This is the default value that will be set for this property. Changing it has immediate effect, although
                on large organizations it may take minutes to be available in search results
              </FormControl.Caption>
            </FormControl>
          )}
        </Box>
      </Box>
      <Box sx={{display: 'flex', gap: 2}}>
        <Button
          variant="primary"
          disabled={saving}
          onClick={() =>
            saveDefinition({
              propertyName: propertyNameField.value,
              valueType: definitionTypeField.value as ValueType,
              required: isRequired,
              defaultValue: isRequired ? propertyDefaultValueField.value : null,
              description: propertyDescriptionField.value.trim() || null,
              allowedValues: isSingleSelect || isMultiSelect ? allowedValues : null,
              valuesEditableBy: repoActorsEditingAllowed ? 'org_and_repo_actors' : 'org_actors',
            })
          }
        >
          {saving ? 'Saving...' : 'Save property'}
        </Button>

        <Button onClick={returnToList}>Cancel</Button>
      </Box>
    </>
  )
}

const CANNOT_SEARCH_VALUES = ['none']

function isNonSearchable(value: string) {
  return CANNOT_SEARCH_VALUES.includes(value?.toLowerCase())
}

function AllowedValueItem({
  value,
  isDefaultValue,
  onDelete,
  index,
}: {
  value: string
  isDefaultValue: boolean
  onDelete(): void
  index: number
}) {
  const {openMoveModal} = useDragAndDrop()

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        wordBreak: 'break-all',
        alignItems: 'center',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
        <DragAndDrop.DragTrigger />
        {value}
        {isDefaultValue && <Label variant="accent">Default</Label>}
        {isNonSearchable(value) && <Label variant="attention">Non-searchable</Label>}
      </Box>
      <Box>
        <ActionMenu>
          <ActionMenu.Anchor>
            <IconButton icon={KebabHorizontalIcon} variant="invisible" aria-label={`More options for ${value}`} />
          </ActionMenu.Anchor>

          <ActionMenu.Overlay>
            <ActionList>
              <ActionList.Item onSelect={() => openMoveModal(value, index)} aria-label={`Move option ${value}`}>
                Move
              </ActionList.Item>
              <ActionList.Item variant="danger" onSelect={onDelete} aria-label={`Delete option ${value}`}>
                Delete
              </ActionList.Item>
            </ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      </Box>
    </Box>
  )
}

try{ PropertyDefinitionSettings.displayName ||= 'PropertyDefinitionSettings' } catch {}
try{ AllowedValueItem.displayName ||= 'AllowedValueItem' } catch {}